import { getResolvedBuildingVersion } from '../actions/resolved-version.helpers';
import { Baseline, ProjectMetadata } from '../../models/project.model';
import {
  EnergyExpressionVariables,
  ENERGY_VARIABLE_NAME,
} from './energy-expression-variables.models';
import { ExpressionVariables } from '../expression-variables.helpers';
import { replaceProperties } from '../object.helpers';
import { getBuildingHeatingDemand } from './energy.helpers';
import { EnvironmentHourData } from './weather.models';
import { cacheFactory } from '../function.helpers';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { IProposal } from '../../models/proposals.model';

/**
 * Apply energy expression variables to the project variables.
 * @param version - The building version.
 * @param meta - The project metadata.
 * @param projectVars - The project variables.
 * @param weatherHours - The weather hours.
 * @returns The energy expression variables.
 */
/** @param proposal When set, heat demand is computed for this proposal (for per-proposal heating). */
export const applyEnergyExpressionVariables = (
  version: Baseline,
  meta: ProjectMetadata,
  projectVars: ExpressionVariables,
  weatherHours: EnvironmentHourData[],
  design?: BuildingDesign,
  proposal?: IProposal,
): ExpressionVariables =>
  replaceProperties(
    projectVars,
    getEnergyExpressionVariables(
      version,
      meta,
      weatherHours,
      projectVars[EXPRESSION_VARIABLE_NAME.HEATED_AREA],
      design,
      proposal,
    ),
  );

/**
 * Get the energy expression variables.
 * @param version - The building version.
 * @param meta - The project metadata.
 * @param projectVars - The project variables.
 * @param weatherHours - The weather hours.
 * @param proposal When provided, envelope/heat demand is computed for this proposal (for per-proposal heating demand).
 * @returns The energy expression variables.
 */
export const getEnergyExpressionVariables = (
  version: Baseline,
  meta: ProjectMetadata,
  environmentHours: EnvironmentHourData[],
  heatedArea: number,
  design?: BuildingDesign,
  proposal?: IProposal,
): EnergyExpressionVariables => {
  const proposalId = proposal?.id ?? '';
  return cacheFactory(
    () => {
      // Envelope areas must reflect proposal quantity/property overrides (baseline tree + proposal is not enough).
      const versionForDemand = proposal
        ? getResolvedBuildingVersion(version, proposal)
        : version;

      const demand = getBuildingHeatingDemand(versionForDemand, {
        metadata: design ? undefined : meta,
        environmentHours,
        heatedArea,
        design,
        proposal,
      });

      return {
        [ENERGY_VARIABLE_NAME.HEATING_DEMAND_SPACE]: demand.heatingDemand,
        [ENERGY_VARIABLE_NAME.HEATING_DEMAND_TAPWATER]:
          demand.tapwaterHeatingDemand,
        [ENERGY_VARIABLE_NAME.POST_HEATING_DEMAND]: demand.postHeatingDemand,
        [ENERGY_VARIABLE_NAME.COOLING_DEMAND_SPACE]: demand.coolingDemand,
        [ENERGY_VARIABLE_NAME.PRE_COOLING_DEMAND]: demand.preCoolingDemand,
      };
    },
    `getEnergyExpressionVariables[${version.id}][${proposalId}]`,
    [version, meta, environmentHours, heatedArea, design, proposal],
  );
};
